import { type FC, type ReactNode, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Link } from "@root/shared/components/Link";
import { Button } from "@root/shared/components/Button";
import { MultipleChoiceQuestion } from "./uiParts/MultipleChoiceQuestion";
import { NewShopGroup } from "./uiParts/NewShopGroup";
import {
  NEW_SHOP_GROUP_TRIGGER,
  SIBLING_OR_REQUIRED_QUESTION_CODES,
  followUpKey,
  monthKey,
  yearKey,
  type HoldEnqueteFormValues,
} from "./schema";
import { type PresentationProps, type Question } from "./types";
import * as styles from "./styles.css";

// 選択肢を 4 列グリッドで並べたい子設問のコード。増減はこの Set をいじるだけで済む。
const GRID_LAYOUT_QUESTION_CODES = new Set<string>(["q1_route", "q3_undecided_preferred_media"]);

// 「※選択済みの休止期間：...」の補足文言を見出し直下に出したい設問のコード（Q3 系）。
const HOLD_PERIOD_NOTE_QUESTION_CODES = new Set<string>([
  "q3_specific_reason",
  "q3_undecided_reason",
]);

type BuildChildrenArgs = {
  parent: Question;
  allQuestions: Question[];
  values: Partial<HoldEnqueteFormValues>;
  baseYear: number | undefined;
  baseMonth: number | undefined;
};

// 現在の値と設問一覧から、指定した親設問の選択肢ごとに
// 「その選択肢を選んだ時に直下へ差し込む要素」を組み立てて返す。
const buildChildrenByOptionCode = ({
  parent,
  allQuestions,
  values,
  baseYear,
  baseMonth,
}: BuildChildrenArgs): Record<string, ReactNode> => {
  const selected = values[parent.questionCode];
  const selectedOptionCodes = Array.isArray(selected) ? selected : [];
  const result: Record<string, ReactNode> = {};

  parent.options.forEach((option) => {
    if (!selectedOptionCodes.includes(option.optionCode)) return;

    // 「新店オープン」直下は特殊: 期間・エリアの 2 設問をまとめて新店グループとして描画する。
    if (
      parent.questionCode === NEW_SHOP_GROUP_TRIGGER.questionCode &&
      option.optionCode === NEW_SHOP_GROUP_TRIGGER.optionCode
    ) {
      const period = allQuestions.find(
        (q) => q.questionCode === SIBLING_OR_REQUIRED_QUESTION_CODES[0],
      );
      const area = allQuestions.find(
        (q) => q.questionCode === SIBLING_OR_REQUIRED_QUESTION_CODES[1],
      );
      if (period && area) {
        result[option.optionCode] = (
          <NewShopGroup
            periodName={period.questionCode}
            yearName={yearKey(period.questionCode)}
            monthName={monthKey(period.questionCode)}
            areaName={area.questionCode}
            areaLabel={area.text}
            baseYear={baseYear}
            baseMonth={baseMonth}
          />
        );
      }
      return;
    }

    const children = allQuestions.filter(
      (q) =>
        q.parentQuestionCode === parent.questionCode && q.parentOptionCode === option.optionCode,
    );
    if (children.length === 0) return;

    result[option.optionCode] = (
      <>
        {children.map((child) => (
          <MultipleChoiceQuestion
            key={child.questionCode}
            question={child.text}
            isChildStyle
            options={child.options}
            isGridLayout={GRID_LAYOUT_QUESTION_CODES.has(child.questionCode)}
            formField={{
              name: child.questionCode,
              followUpTextName: followUpKey(child.questionCode),
            }}
          />
        ))}
      </>
    );
  });

  return result;
};

export const Presentation: FC<PresentationProps> = ({
  questions,
  backHref,
  holdPeriodText,
  baseYear,
  baseMonth,
}) => {
  const { formState, watch } = useFormContext<HoldEnqueteFormValues>();
  const values = watch();

  // 番号を振る対象はトップレベル設問だけ
  const topLevelQuestions = useMemo(
    () => questions.filter((q) => q.parentQuestionCode === null),
    [questions],
  );

  return (
    <>
      <div className={styles.questions}>
        {topLevelQuestions.map((question, index) => {
          const label = `${index + 1}.${question.text}`;
          const childrenByOptionCode = buildChildrenByOptionCode({
            parent: question,
            allQuestions: questions,
            values,
            baseYear,
            baseMonth,
          });
          // Q3（具体日／未定 いずれも）の見出し直下に「※選択済みの休止期間：...」を出す。
          // 未定のときはサーバ側で「開始日から未定（無期限）」の文字列を組み立てて渡してくる。
          const note =
            HOLD_PERIOD_NOTE_QUESTION_CODES.has(question.questionCode) && holdPeriodText
              ? `※選択済みの休止期間：${holdPeriodText}`
              : undefined;
          return (
            <div key={question.questionCode}>
              <MultipleChoiceQuestion
                question={label}
                note={note}
                options={question.options}
                formField={{
                  name: question.questionCode,
                  followUpTextName: followUpKey(question.questionCode),
                }}
                childrenByOptionCode={childrenByOptionCode}
              />
            </div>
          );
        })}
      </div>

      <div className={styles.buttons}>
        <Link theme="tertiary" href={backHref} className={styles.button}>
          戻る
        </Link>
        <Button
          type="submit"
          theme={formState.isSubmitting ? "disabled" : "primary"}
          disabled={formState.isSubmitting}
          className={styles.button}
        >
          次へ
        </Button>
      </div>
    </>
  );
};
